import { z } from "zod";

/**
 * Histórico de alterações e marcos: o contrato entre a API e as telas de histórico, atividade
 * por pessoa e auditoria. Ver ADR 0006.
 */
export type ChangeKind = "edit" | "create" | "delete" | "rename" | "move";
export type ChangeVia = "editor" | "terminal" | "suggestion";

export const CHANGE_KIND_LABELS: Record<ChangeKind, string> = {
  edit: "editou",
  create: "criou",
  delete: "apagou",
  rename: "renomeou",
  move: "moveu",
};

export const CHANGE_VIA_LABELS: Record<ChangeVia, string> = {
  editor: "no editor",
  terminal: "pelo terminal",
  suggestion: "por sugestão aceita",
};

/** Uma alteração sem atualização há mais que isso deixa de ser "agora". */
export const LIVE_WINDOW_MS = 3 * 60_000;

export interface PersonRef {
  id: string;
  handle: string;
  name: string;
  avatarUrl: string | null;
  color: string;
}

export interface ChangeAuthor extends PersonRef {
  via: ChangeVia;
  charsAdded: number;
  charsRemoved: number;
  linesAdded: number;
  linesRemoved: number;
}

export interface FileChangeItem {
  id: string;
  projectId: string;
  /** Preenchido nos feeds que juntam vários projetos (grupo). */
  projectName?: string;
  fileId: string;
  kind: ChangeKind;
  nodeType: "file" | "folder";
  path: string;
  oldPath: string | null;
  startedAt: string;
  updatedAt: string;
  endedAt: string | null;
  linesAdded: number;
  linesRemoved: number;
  /** Em andamento agora (janela aberta e com atividade recente). */
  live: boolean;
  authors: ChangeAuthor[];
}

export interface FileChangeDetail extends FileChangeItem {
  patch: string | null;
  patchTruncated: boolean;
}

export interface HistoryPage {
  items: FileChangeItem[];
  nextCursor: string | null;
}

export const HistoryQuery = z.object({
  cursor: z
    .string()
    .regex(/^\d{1,18}$/)
    .optional(),
  userId: z.uuid().optional(),
  fileId: z.string().min(1).max(64).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(40),
});

export const ACTIVITY_PERIODS = [1, 7, 30] as const;
export const ActivityQuery = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => (ACTIVITY_PERIODS as readonly number[]).includes(d), "Período inválido.")
    .default(7),
});

export interface LiveWork {
  projectId: string;
  projectName?: string;
  fileId: string;
  path: string;
  since: string;
  linesAdded: number;
  linesRemoved: number;
}

export interface PersonActivity {
  person: PersonRef;
  /** Papel no projeto (atividade de projeto) ou no grupo (atividade de grupo). */
  role: string;
  online: boolean;
  /** O que a pessoa está alterando agora. */
  editingNow: LiveWork[];
  changes: number;
  files: number;
  linesAdded: number;
  linesRemoved: number;
  charsAdded: number;
  charsRemoved: number;
  lastAt: string | null;
}

export interface ProjectActivity {
  days: number;
  people: PersonActivity[];
}

export interface GroupActivity {
  days: number;
  people: PersonActivity[];
  recent: FileChangeItem[];
}

// ───────────── Marcos ─────────────

export const CHECKPOINT_LIMITS = {
  maxMessage: 500,
  /** Tamanho máximo do arquivo comprimido de um marco. */
  maxArchiveBytes: 25 * 1024 * 1024,
  /** Diffs calculados por marco (o resto aparece só na lista). */
  maxDiffFiles: 200,
} as const;

export const CreateCheckpointInput = z.object({
  message: z.string().trim().min(1, "Descreva o marco.").max(CHECKPOINT_LIMITS.maxMessage),
});
export type CreateCheckpointInput = z.infer<typeof CreateCheckpointInput>;

export interface CheckpointFileChange {
  path: string;
  status: "added" | "removed" | "modified";
  linesAdded: number;
  linesRemoved: number;
}

export interface CheckpointAuthor extends PersonRef {
  changes: number;
  linesAdded: number;
  linesRemoved: number;
}

export interface CheckpointSummary {
  number: number;
  message: string;
  createdAt: string;
  createdBy: PersonRef | null;
  files: number;
  changedFiles: CheckpointFileChange[];
  linesAdded: number;
  linesRemoved: number;
  /** Quem alterou o projeto desde o marco anterior (os "coautores"). */
  authors: CheckpointAuthor[];
}

export interface CheckpointDetail extends CheckpointSummary {
  diffs: Array<CheckpointFileChange & { patch: string | null; truncated: boolean }>;
}
